import React, { FC, useMemo } from 'react';
import moment from 'moment';
import { ReactTable, OptionsDropdown, DefaultColumnType } from '@td-client-frontend/common';
import { useStopJob } from '../../../hooks/useJob';
import { ExclamationInTriangle } from '@td-client-frontend/icons';
import Modal from '../../../components/Modal/NewModal';
import { Warnings } from './Warnings';
import { statusFormat } from '../../../utils/jobs/helpers';
import { useWarnings } from '../../../hooks/useWarnings';

interface JobsTableProps {
  data: any; // type reseller data
  onDownload: ({ id, fileName }: { id: string; fileName: string }) => void;
  onReview: (id: string) => void;
  onReupload: (auditId: string) => void;
  getValidationErrors: (id: string) => void;
}

interface DropdownOption {
  label: string;
  onClick: () => void;
}

export const JobTable: FC<JobsTableProps> = ({ data, onReview, onReupload, onDownload, getValidationErrors }) => {
  const { stopJob } = useStopJob();
  const { isOpenWarningsModal, job, openWarningsModal, closeWarningsModal } = useWarnings();

  const stopCondition = (row: Record<string, any>) => {
    if (['approved', 'deferred'].includes(row.Status)) {
      return true;
    }
    const createdAt = new Date(row.CreatedAt).getTime();
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    if (row.Status !== 'completed' && createdAt < oneHourAgo) {
      return true;
    }
    return false;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getOptionsForRow = (row: Record<string, any>) => {
    const options: DropdownOption[] = [];
    if (row.Status.includes('error')) options.push({ label: 'Reupload', onClick: () => onReupload(row.AuditId) });
    if (stopCondition(row)) options.push({ label: 'Stop', onClick: () => stopJob(row) });
    if (row.Status === 'pending') options.push({ label: 'Review', onClick: () => onReview(row._id) });
    if (row.ZipFilePath)
      options.push({
        label: 'Download',
        onClick: () => onDownload({ id: row._id, fileName: row.ZipFileName })
      });
    if (row.ValidationErrors) options.push({ label: 'Validation Errors', onClick: () => getValidationErrors(row._id) });
    return options;
  };

  const dependentColumns: DefaultColumnType = useMemo(() => {
    const statusWithWarning = (row: Record<string, any>) => {
      return (
        <div className="flex items-center w-full">
          {statusFormat(row.original.Status)}
          <ExclamationInTriangle
            fill="#f6be00"
            onClick={() => openWarningsModal(row.original)}
            className="w-5 h-5 ml-2 cursor-pointer"
          />
        </div>
      );
    };
    return [
      {
        Header: 'Filename',
        accessor: 'ZipFileName'
      },
      {
        Header: 'Status',
        accessor: ({ Status }) => {
          return statusFormat(Status);
        },
        Cell: ({ row }) => {
          return row.original.Error ? statusWithWarning(row) : statusFormat(row.original.Status);
        }
      },
      {
        Header: 'Created by',
        accessor: 'Client'
      },
      {
        Header: 'Created date',
        accessor: 'CreatedAt',
        Cell: ({ row }) => {
          return moment(row.original.CreatedAt).format('MM/DD/YYYY hh:mm a');
        }
      },
      {
        Header: 'Updated date',
        accessor: 'UpdatedAt',
        Cell: ({ row }) => {
          return moment(row.original.UpdatedAt).format('MM/DD/YYYY hh:mm a');
        }
      },
      {
        Header: 'Actions',
        disableSortBy: true,
        Cell: ({ row }) => {
          const options = getOptionsForRow(row.original);
          return (
            <OptionsDropdown
              disabled={!options?.length}
              options={options}
              label="Manage"
              styles="text-primary-500 font-medium pl-0"
              position="left center"
              dropdownStyles="border-primary-500 mr-2"
            />
          );
        },
        width: 50
      }
    ];
  }, [getOptionsForRow, openWarningsModal]);

  return (
    <>
      <ReactTable columns={dependentColumns} data={data} getSortBy manualSortBy />
      <Modal
        isOpen={isOpenWarningsModal}
        onClose={closeWarningsModal}
        header="Warnings"
        customFooterActions={{
          right: job?.ZipFileName,
          left: (
            <span className="text-gray-40">
              {' '}
              {statusFormat(job?.Status)}: updated at {moment(job?.UpdatedAt).format('MM/DD/YYYY hh:mm a')}
            </span>
          )
        }}
        size="large"
      >
        {job?.Error && <Warnings warnings={job?.Error} />}
      </Modal>
    </>
  );
};
